import { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabase';

// ─── Generic hook for any table ───
function useTable(table) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    const { data: rows, error } = await supabase
      .from(table)
      .select('*')
      .order('created_at', { ascending: false });
    if (error) console.error(`Error fetching ${table}:`, error);
    else setData(rows || []);
    setLoading(false);
  }, [table]);

  useEffect(() => { fetch(); }, [fetch]);

  const add = async (item) => {
    const { data: row, error } = await supabase
      .from(table)
      .insert(item)
      .select()
      .single();
    if (error) { console.error(`Error adding to ${table}:`, error); return null; }
    setData(prev => [row, ...prev]);
    return row;
  };

  const update = async (id, updates) => {
    const { data: row, error } = await supabase
      .from(table)
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    if (error) { console.error(`Error updating ${table}:`, error); return null; }
    setData(prev => prev.map(r => r.id === id ? row : r));
    return row;
  };

  const remove = async (id) => {
    const { error } = await supabase
      .from(table)
      .delete()
      .eq('id', id);
    if (error) { console.error(`Error deleting from ${table}:`, error); return false; }
    setData(prev => prev.filter(r => r.id !== id));
    return true;
  };

  return { data, loading, fetch, add, update, remove };
}

// ─── Specific hooks for each entity ───
export function useInvoices() {
  return useTable('invoices');
}

export function useExpenses() {
  return useTable('expenses');
}

export function useClients() {
  return useTable('clients');
}

export function useBills() {
  return useTable('bills');
}

export function useBizInfo() {
  const [bizInfo, setBizInfo] = useState({ name: '', abn: '', email: '', phone: '', address: '', bank_name: '', account_name: '', bsb: '', account_number: '', logo_url: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBiz = async () => {
      const { data, error } = await supabase
        .from('business_info')
        .select('*')
        .limit(1)
        .maybeSingle();  // maybeSingle returns null instead of erroring when no rows
      if (error) console.error('Error fetching business info:', error);
      if (data) setBizInfo(data);
      setLoading(false);
    };
    fetchBiz();
  }, []);

  const saveBizInfo = async (info) => {
    // Only send the editable fields — strip out system columns
    const { id, user_id, created_at, ...editableFields } = info;

    if (id) {
      // Update existing row
      const { data, error } = await supabase
        .from('business_info')
        .update(editableFields)
        .eq('id', id)
        .select()
        .single();
      if (error) console.error('Error updating business info:', error);
      if (data) setBizInfo(data);
      else setBizInfo(info); // optimistic update
    } else {
      // Insert new row
      const { data, error } = await supabase
        .from('business_info')
        .insert(editableFields)
        .select()
        .single();
      if (error) console.error('Error inserting business info:', error);
      if (data) setBizInfo(data);
      else setBizInfo(info); // optimistic update
    }
  };

  return { bizInfo, loading, saveBizInfo };
}
